import { useMemo, useRef, useState } from 'react';
import { Textarea } from './ui/textarea';
import { cn } from '../lib/utils';

/**
 * Textarea with inline @-mention autocomplete. Type "@" then a name → pick from
 * the dropdown (↑/↓ + Enter/Tab, or click). Selecting inserts "@Name " and calls
 * onMention(user) so the parent can track the mention list.
 */
export default function MentionTextarea({ value, onChange, users = [], onMention,
                                         placeholder, rows = 3, className, textareaClassName,
                                         onFocus, onBlur, autoFocus }) {
  const ref = useRef(null);
  const [menu, setMenu] = useState(null);   // { query, start } | null
  const [active, setActive] = useState(0);

  const matches = useMemo(() => {
    if (!menu) return [];
    const q = menu.query.toLowerCase();
    return users
      .filter((u) => (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q))
      .slice(0, 6);
  }, [menu, users]);

  const detect = (text, caret) => {
    const m = text.slice(0, caret).match(/(?:^|\s)@([\w.\-]*)$/);
    if (!m) return null;
    return { query: m[1], start: caret - m[1].length - 1 };
  };

  const handleChange = (e) => {
    onChange(e.target.value);
    setMenu(detect(e.target.value, e.target.selectionStart));
    setActive(0);
  };

  const pick = (u) => {
    if (!menu) return;
    const caret = ref.current?.selectionStart ?? value.length;
    const next = `${value.slice(0, menu.start)}@${u.name} ${value.slice(caret)}`;
    onChange(next);
    onMention?.(u);
    setMenu(null);
    setTimeout(() => ref.current?.focus(), 0);
  };

  const onKeyDown = (e) => {
    if (!menu || matches.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => (a + 1) % matches.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => (a - 1 + matches.length) % matches.length); }
    else if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pick(matches[active]); }
    else if (e.key === 'Escape') { setMenu(null); }
  };

  return (
    <div className={cn('relative', className)}>
      <Textarea ref={ref} rows={rows} placeholder={placeholder} value={value} autoFocus={autoFocus}
                className={textareaClassName}
                onChange={handleChange} onKeyDown={onKeyDown} onFocus={onFocus}
                onBlur={(e) => { setTimeout(() => setMenu(null), 150); onBlur?.(e); }} />
      {menu && matches.length > 0 && (
        <div className="absolute left-2 top-full z-30 mt-1 max-h-56 w-64 overflow-y-auto rounded-lg border border-border bg-popover p-1 shadow-pop">
          {matches.map((u, i) => (
            <button key={u.id} type="button"
                    onMouseDown={(e) => { e.preventDefault(); pick(u); }}
                    className={cn('block w-full truncate rounded px-2 py-1.5 text-left text-[13px]',
                      i === active ? 'bg-muted' : 'hover:bg-muted')}>
              {u.name} <span className="text-muted-foreground">· {u.email}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
